'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AdSlot from '@/components/AdSlot';


interface FAQItem {
  question: string;
  answer: string;
}

interface ToolPageLayoutProps {
  title: string;
  description: string;
  category: string;
  categoryHref: string;
  faqItems?: FAQItem[];
  howToSteps?: string[];
  structuredData?: object;

  // Named content slots
  uploadArea?: React.ReactNode;
  settingsArea?: React.ReactNode;
  resultPreview?: React.ReactNode;
  downloadArea?: React.ReactNode;

  // Legacy fallback
  children?: React.ReactNode;
}

export default function ToolPageLayout({
  title,
  description,
  category,
  categoryHref,
  faqItems,
  howToSteps,
  structuredData,
  uploadArea,
  settingsArea,
  resultPreview,
  downloadArea,
  children,
}: ToolPageLayoutProps) {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const hasSlots = uploadArea || settingsArea || resultPreview || downloadArea;

  return (
    <div className="min-h-screen relative">
      {/* Breadcrumb */}
      <div className="tool-breadcrumb-bar">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-2 text-sm text-text-muted overflow-x-auto whitespace-nowrap">
          <Link href="/" className="hover:text-brand-blue transition-colors shrink-0">Home</Link>
          <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
          <Link href={categoryHref} className="hover:text-brand-blue transition-colors shrink-0">{category}</Link>
          <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
          <span className="text-text-primary font-medium truncate">{title}</span>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8 relative z-10">

        {/* ── Tool Header ── */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-text-primary mb-2 leading-tight">{title}</h1>
          <p className="text-text-secondary text-base leading-relaxed max-w-2xl">{description}</p>

          {/* Privacy Badge */}
          <div className="privacy-badge mt-3">
            <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
            </svg>
            Processed locally in your browser
          </div>
        </div>

        {/* ── Tool Card ── */}
        {hasSlots ? (
          <div className="tool-main-card rounded-3xl overflow-hidden mb-8">
            {/* Upload Area */}
            {uploadArea && (
              <div className="tool-card-section tool-card-section-border p-5 sm:p-7">
                <p className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-4">Upload File</p>
                {uploadArea}
              </div>
            )}

            {/* Settings Area */}
            {settingsArea && (
              <div className="tool-card-section tool-card-section-alt tool-card-section-border p-5 sm:p-7">
                <p className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-4">Settings</p>
                {settingsArea}
              </div>
            )}

            {/* Result Preview Area */}
            {resultPreview && (
              <div className="tool-card-section tool-card-section-border p-5 sm:p-7">
                <p className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-4">Preview</p>
                {resultPreview}
              </div>
            )}

            {/* Download Button Area */}
            {downloadArea && (
              <div className="tool-card-section tool-card-section-alt p-5 sm:p-7">
                {downloadArea}
              </div>
            )}
          </div>
        ) : children ? (
          /* Legacy children fallback */
          <div className="tool-main-card rounded-3xl p-5 sm:p-8 mb-8">
            {children}
          </div>
        ) : null}

        {/* ── How to Use ── */}
        {howToSteps && howToSteps.length > 0 && (
          <div className="tool-info-card rounded-3xl p-5 sm:p-7 mb-6">
            <h2 className="text-lg font-bold text-text-primary mb-5">How to Use</h2>
            <ol className="space-y-0">
              {howToSteps.map((step, i) => (
                <li key={i} className="flex gap-4 group">
                  <div className="flex flex-col items-center">
                    <div
                      className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 z-10"
                      style={{
                        background: 'linear-gradient(135deg, #2563EB 0%, #1d4ed8 100%)',
                        boxShadow: '0 2px 8px rgba(37,99,235,0.3)',
                      }}
                    >
                      <span className="text-white text-xs font-bold">{i + 1}</span>
                    </div>
                    {i < howToSteps.length - 1 && (
                      <div className="w-px flex-1 tool-step-connector my-1" />
                    )}
                  </div>
                  <div className="pb-5 pt-0.5 flex-1">
                    <p className="text-text-secondary text-sm leading-relaxed">{step}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* ── Ad Slot ── */}
        <AdSlot type="horizontal" className="mb-6" />

        {/* ── FAQ ── */}
        {faqItems && faqItems.length > 0 && (
          <div className="tool-info-card rounded-3xl p-5 sm:p-7 mb-6">
            <h2 className="text-lg font-bold text-text-primary mb-5">Frequently Asked Questions</h2>
            <div className="space-y-2">
              {faqItems.map((item, i) => (
                <div key={i} className="tool-faq-item rounded-2xl overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="tool-faq-btn w-full flex items-center justify-between gap-3 px-4 py-3.5 text-left"
                    aria-expanded={openFaq === i}
                  >
                    <span className="font-semibold text-text-primary text-sm">{item.question}</span>
                    <svg
                      className={`w-4 h-4 text-text-muted shrink-0 transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`}
                      fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {openFaq === i && (
                    <div className="tool-faq-answer px-4 pb-4 pt-1 animate-fade-in-up">
                      <p className="text-text-secondary text-sm leading-relaxed">{item.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Structured data */}
        {structuredData && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
          />
        )}
      </div>
    </div>
  );
}
